import React, { useEffect, useState } from "react";
import { Ticket, Tag, Star } from "lucide-react";
import { api } from "../../redux/apis/api";

// Read-only benefits breakdown for VIEW mode. The create/edit payment sidebar
// renders the interactive SchemePanel / CouponPanel / LoyaltyPanel; a saved
// bill instead shows what was actually applied, fetched from the audit tables
// via GET /order/bill-benefits/:sot_id (coupon_usage → which code,
// bill_offer_application → which scheme, bill row → points count + amounts).
// All-local reads on the backend, so this works offline at a store.

const fmtINR = (n) => Number(n || 0).toFixed(2);

const Card = ({ icon, iconBg, label, labelColor, title, sub, amount, reversed }) => (
  <div
    className={`rounded-xl border px-3 py-2.5 mb-3 flex items-center gap-3 ${
      reversed
        ? "border-[#E2E8F0] bg-[#F8FAFC] opacity-75"
        : "border-[#BBE7CC] bg-gradient-to-br from-[#F0FBF4] to-[#E6F6EC]"
    }`}
  >
    <div
      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
      style={{ background: iconBg, color: "white" }}
    >
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div
        className="text-[11px] uppercase tracking-wider font-bold"
        style={{ color: reversed ? "#64748B" : labelColor }}
      >
        {label}
        {reversed && " · reversed on return"}
      </div>
      <div className="text-[14px] font-bold text-[#0F172A] mt-0.5 flex items-center gap-2 flex-wrap">
        {title && <span className="font-mono tracking-wide">{title}</span>}
        {sub && (
          <span className="text-[12px] font-semibold text-[#475569] truncate">{sub}</span>
        )}
        <span className={`text-[13px] ${reversed ? "line-through text-[#64748B]" : "text-[#15803C]"}`}>
          − ₹{fmtINR(amount)}
        </span>
      </div>
    </div>
  </div>
);

const UsedBenefitsSummary = ({ sotId }) => {
  const [benefits, setBenefits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sotId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get(`/order/bill-benefits/${sotId}`)
      .then((res) => {
        if (!cancelled) setBenefits(res?.data?.data || null);
      })
      .catch(() => {
        if (!cancelled) setBenefits(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sotId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 mb-3 text-[12px] text-[#64748B]">
        Loading benefits…
      </div>
    );
  }

  const { scheme, coupon, points } = benefits || {};
  if (!scheme && !coupon && !points) return null;

  return (
    <div className="mb-2">
      <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-bold mb-2">
        Benefits applied on this bill
      </div>
      {scheme && (
        <Card
          icon={<Tag size={18} />}
          iconBg="linear-gradient(135deg, #8B5CF6, #6D28D9)"
          label="Scheme"
          labelColor="#6D28D9"
          title={scheme.name || "Scheme"}
          sub={scheme.free_mrp > 0 ? `free items worth ₹${fmtINR(scheme.free_mrp)}` : null}
          amount={scheme.amount}
          reversed={scheme.reversed}
        />
      )}
      {coupon && (
        <Card
          icon={<Ticket size={18} />}
          iconBg="linear-gradient(135deg, #22C55E, #15803C)"
          label="Coupon"
          labelColor="#15803C"
          title={coupon.code || "Coupon"}
          sub={coupon.name}
          amount={coupon.amount}
          reversed={coupon.reversed}
        />
      )}
      {points && (
        <Card
          icon={<Star size={18} />}
          iconBg="linear-gradient(135deg, #F59E0B, #B45309)"
          label="Loyalty points"
          labelColor="#B45309"
          title={points.points != null ? `${points.points} pts` : null}
          sub={points.points == null ? "points redeemed" : null}
          amount={points.amount}
          reversed={points.reversed === true}
        />
      )}
    </div>
  );
};

export default UsedBenefitsSummary;
